import React, { useState } from "react";
import { Flex, Select } from "antd";

import EntitySelect from "../../../component/EntitySelect";
import { POINTS_ADMIN } from "../../../config/lang";
import { translate } from "../../../util/util";
import { AUDIENCE_TARGET_TYPES } from "./questionOptions";

// A survey targets exactly one of points / point_groups / networks (enforced server-side).
// This field shows a single target-type selector plus one multi-select for whichever type is
// active, and clears the other two keys on change so the invalid multi-target combination can
// never be produced from this UI. It writes directly into item.points/point_groups/networks
// rather than into its own "audience" key.
const hasValue = v => Array.isArray(v) && v.length > 0;

// The pickers report selections as option OBJECTS; the backend M2M fields expect pks, so
// normalize to ids before writing (mirrors QuestionsField.toIds).
const toIds = list => (list || []).map(x => (x && typeof x === "object" ? x.id ?? x.value ?? x : x));

export default function AudienceField({ item = {}, onChange, lang }) {
  // Hold the active target type in state. Deriving it from item every render made an empty
  // selection (e.g. right after switching to "Point groups") snap back to the default type,
  // so the point_groups/networks dropdowns could never be used.
  const [activeKey, setActiveKey] = useState(() => (AUDIENCE_TARGET_TYPES.find(t => hasValue(item[t.key])) || AUDIENCE_TARGET_TYPES[0]).key);
  const active = AUDIENCE_TARGET_TYPES.find(t => t.key === activeKey) || AUDIENCE_TARGET_TYPES[0];
  const typeLabel = translate(POINTS_ADMIN, "audience_target_type", lang);

  const setTargetType = key => {
    setActiveKey(key);
    AUDIENCE_TARGET_TYPES.forEach(t => onChange(t.key, t.key === key ? toIds(item[t.key]) : []));
  };

  return (
    <Flex vertical gap="small">
      {/* The three options name themselves ("Points" / "Point groups" / "Networks"), so the
          type select carries its label as an accessible name rather than a second visible one
          inside a field that already has one. */}
      <Select
        aria-label={typeLabel}
        value={activeKey}
        onChange={setTargetType}
        options={AUDIENCE_TARGET_TYPES.map(t => ({ value: t.key, label: translate(POINTS_ADMIN, t.labelKey, lang) }))}
      />
      <EntitySelect
        entity={active.entity}
        value={item[active.key] || []}
        isMulti
        extraQuery={{ active: true }}
        ariaLabel={translate(POINTS_ADMIN, active.labelKey, lang)}
        onChange={option => onChange(active.key, toIds(option))}
      />
    </Flex>
  );
}
